import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Props = {
  /** Der sichtbare Auslöser, meist ein Knopf. Ein Klick darauf öffnet bzw. schließt. */
  ausloeser: ReactNode;
  offen: boolean;
  onOffen(offen: boolean): void;
  children: ReactNode;
  /** Name des aufgeklappten Inhalts für Screenreader. */
  label?: string;
  /** Zusatzklasse für den Inhalt, z. B. eine Breite. */
  className?: string;
};

const RAND = 8;
const ABSTAND = 4;

/** Per Tab erreichbare Bedienelemente im Inhalt. */
const TABBAR =
  "input:not([disabled]):not([type=hidden]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])";

/**
 * Schlichtes Popover ohne Bibliothek: schließt bei Klick außerhalb, bei Escape und wenn der
 * Fokus hinausgeht. Der Inhalt hängt per Portal direkt an `body` und steht fest am Bildschirm
 * unter dem Auslöser – so schneidet ihn kein Tabellenrahmen mit `overflow` ab, und ein Vorfahr
 * mit `transform` oder Maske (z. B. das Blatt) verschiebt oder beschneidet ihn nicht.
 * Deshalb gestalten ihn nur Klassen am Inhalt selbst, keine Selektoren über Vorfahren.
 * Für die Tab-Taste steht der Inhalt trotzdem direkt hinter dem Auslöser.
 */
export function Popover({ ausloeser, offen, onOffen, children, label, className }: Props) {
  const wurzel = useRef<HTMLSpanElement>(null);
  const inhalt = useRef<HTMLDivElement>(null);
  const [lage, setLage] = useState<CSSProperties | null>(null);

  // Immer die neueste Rückmeldung, ohne die Listener bei jedem Rendern neu anzumelden.
  const onOffenRef = useRef(onOffen);
  onOffenRef.current = onOffen;

  const messen = useCallback(() => {
    const w = wurzel.current;
    const i = inhalt.current;
    if (!w || !i) return;
    const r = w.getBoundingClientRect();
    const breite = i.offsetWidth;
    const hoehe = i.offsetHeight;
    const left = Math.max(RAND, Math.min(r.left, window.innerWidth - breite - RAND));
    // Unten zu wenig Platz: nach oben klappen, wenn es dort passt.
    const passtUnten = r.bottom + ABSTAND + hoehe <= window.innerHeight - RAND;
    const passtOben = r.top - ABSTAND - hoehe >= RAND;
    const top = !passtUnten && passtOben ? r.top - ABSTAND - hoehe : r.bottom + ABSTAND;
    setLage({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!offen) {
      setLage(null);
      return;
    }
    messen();
  }, [offen, messen]);

  useEffect(() => {
    if (!offen) return;
    const i = inhalt.current;
    // Erstes Bedienelement fokussieren, damit die Tastatur gleich im Inhalt weitermacht.
    i?.querySelector<HTMLElement>("input, button, select, textarea, a[href]")?.focus({ preventScroll: true });

    // Innen heißt: im Auslöser oder im Inhalt – der hängt per Portal nicht unter der Wurzel.
    const innen = (ziel: EventTarget | null) =>
      ziel instanceof Node && (!!wurzel.current?.contains(ziel) || !!inhalt.current?.contains(ziel));

    const knopf = () =>
      wurzel.current?.querySelector<HTMLElement>(".stoffe-popover__ausloeser button, .stoffe-popover__ausloeser [tabindex]");

    const beiZeiger = (e: PointerEvent) => {
      if (!innen(e.target)) onOffenRef.current(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onOffenRef.current(false);
        knopf()?.focus();
        return;
      }
      // Tab wie ohne Portal: Auslöser → Inhalt → Feld nach dem Auslöser. Was der Inhalt
      // selbst schon behandelt hat (z. B. die Bildauswahl), bleibt unberührt.
      if (e.key !== "Tab" || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const k = knopf();
      const aktiv = document.activeElement;
      if (!k || !i || !aktiv) return;
      const tabbar = [...i.querySelectorAll<HTMLElement>(TABBAR)];
      if (aktiv === k) {
        if (e.shiftKey || tabbar.length === 0) return;
        e.preventDefault();
        tabbar[0].focus();
      } else if (i.contains(aktiv)) {
        if (e.shiftKey && (tabbar.length === 0 || aktiv === tabbar[0])) {
          e.preventDefault();
          k.focus();
        } else if (!e.shiftKey && (tabbar.length === 0 || aktiv === tabbar[tabbar.length - 1])) {
          // Ohne preventDefault: Der Browser springt vom Auslöser aus weiter, der Fokus geht hinaus und schließt.
          k.focus();
        }
      }
    };
    // Fokus zwischen Auslöser und Inhalt bleibt drin; geht er woandershin, schließt das Popover.
    const beiFokus = (e: FocusEvent) => {
      if (e.relatedTarget && !innen(e.relatedTarget)) onOffenRef.current(false);
    };
    const neuMessen = () => messen();

    const w = wurzel.current;
    document.addEventListener("pointerdown", beiZeiger);
    document.addEventListener("keydown", beiTaste);
    // An beiden: focusout aus dem Inhalt erreicht die Wurzel im DOM nicht mehr.
    w?.addEventListener("focusout", beiFokus);
    i?.addEventListener("focusout", beiFokus);
    window.addEventListener("scroll", neuMessen, true);
    window.addEventListener("resize", neuMessen);
    // Wächst der Inhalt (z. B. ein neuer Wert), Lage neu bestimmen.
    const beobachter = new ResizeObserver(neuMessen);
    if (i) beobachter.observe(i);
    return () => {
      document.removeEventListener("pointerdown", beiZeiger);
      document.removeEventListener("keydown", beiTaste);
      w?.removeEventListener("focusout", beiFokus);
      i?.removeEventListener("focusout", beiFokus);
      window.removeEventListener("scroll", neuMessen, true);
      window.removeEventListener("resize", neuMessen);
      beobachter.disconnect();
    };
  }, [offen, messen]);

  return (
    <span className="stoffe-popover" ref={wurzel}>
      <span className="stoffe-popover__ausloeser" onClick={() => onOffen(!offen)}>
        {ausloeser}
      </span>
      {offen &&
        createPortal(
          <div
            ref={inhalt}
            role="dialog"
            aria-label={label}
            className={className ? `stoffe-popover__inhalt ${className}` : "stoffe-popover__inhalt"}
            // Bis zur ersten Messung unsichtbar, aber fokussierbar (anders als bei visibility: hidden).
            style={lage ?? { opacity: 0 }}
          >
            {children}
          </div>,
          document.body,
        )}
    </span>
  );
}
